/**
 * Amazon Ads — Sponsored Products v3 Column Map (Story 19.14, Task 4.2)
 *
 * Single source of truth for the 30 priority SP metrics + 8 priority SP
 * dimensions defined in AC #8. Every entry uses the EXACT API field name
 * from `_bmad/bmm/data/integration-metrics/amazon-ads.md`.
 *
 * **Defensive contract:** if Amazon ever renames a column, the
 * `amazon_ads_column_drift_detected` warning fires at runtime AND the v2→v3
 * golden snapshot test (sp-column-map.test.ts) fails in CI. See AC #4 + AC #9.
 *
 * Why this file is kept tiny: the value-add of having a single file enumerating
 * exactly which columns ProvenLeap exposes for SP makes "what does this story
 * ship?" instantly answerable for an acquirer or a future engineer (build-to-
 * sell directive § Documentation).
 */

import type { DimensionDefinition, MetricDefinition } from "../adapter";

// ============================================================================
// Report-type union (4 SP report-type IDs supported in 19.14)
// ============================================================================

export const SP_REPORT_TYPE_IDS = [
  "spCampaigns",
  "spAdvertisedProduct",
  "spPurchasedProduct",
  "spTargeting",
] as const;

export type SpReportTypeId = (typeof SP_REPORT_TYPE_IDS)[number];

// ============================================================================
// Metric column map (AC #8 — 30 priority metrics)
// ============================================================================

export type SpMetricDataType = "Integer" | "Currency" | "Percentage" | "Ratio";

export interface SpMetricColumnEntry {
  /** Canonical key (lowerCamelCase to match Amazon's API field names). */
  readonly key: string;
  /** Human-readable label surfaced in widget configs. */
  readonly label: string;
  /** Which SP report type carries this column. */
  readonly reportTypeId: SpReportTypeId;
  /** Exact API field name on the wire. */
  readonly columnName: string;
  /** Data type for downstream formatting. */
  readonly dataType: SpMetricDataType;
  /** Compatible widget IDs (from integration-mapping-directive § 3). */
  readonly compatibleWidgets: readonly string[];
  /**
   * Story 19.16.1 AC #2 — attribution window in days. Sponsored Products
   * supports 1d / 7d / 30d as TOP-LEVEL attribution windows (verified May
   * 2026 via Helium10 + Marin Support + Amazon docs). 14d is column-suffix-
   * only on Same-SKU/Other-SKU/Sales. `undefined` for delivery-only metrics
   * (impressions/clicks/cost/CTR/CPC).
   */
  readonly attributionWindowDays?: 1 | 7 | 14 | 30;
  /** Story 31-31 — aggregation semantics, carried into the MetricDefinition projection. */
  readonly aggregationKind?: MetricDefinition["aggregationKind"];
  readonly ratioComponents?: MetricDefinition["ratioComponents"];
}

/**
 * The 30 priority SP metrics ("agency-reporting golden path") chosen via
 * Round 4 stakeholder QA architect's prioritization. Order matches AC #8 for
 * easy review.
 *
 * Categories:
 *   - SP Core Delivery & Engagement (5): impressions, clicks, cost, CTR, CPC
 *   - SP Purchases (4): purchases7d/14d + unitsSoldClicks7d/14d
 *   - SP Sales — Total (4): sales1d/7d/14d/30d
 *   - SP Performance Ratios (5): ACOS clicks 7d/14d + ROAS clicks 7d/14d + purchase click rate 7d
 *   - SP Other-SKU Sales (4): salesOtherSku 7d/14d + unitsSoldOtherSku 7d/14d
 *   - SP Same-SKU Sales (4): attributedSalesSameSku 7d/14d + purchasesSameSku 7d/14d
 *   - SP 1-Year (4 — only valid on spAdvertisedProduct): impressions/clicks/spend/CPC 1y
 */
export const SP_V3_COLUMN_MAP: Record<string, SpMetricColumnEntry> = {
  // --- Delivery & Engagement (5) ---
  impressions: {
    key: "impressions",
    label: "Impressions",
    reportTypeId: "spCampaigns",
    columnName: "impressions",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  clicks: {
    key: "clicks",
    label: "Clicks",
    reportTypeId: "spCampaigns",
    columnName: "clicks",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  cost: {
    key: "cost",
    label: "Cost (Spend)",
    reportTypeId: "spCampaigns",
    columnName: "cost",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  clickThroughRate: {
    key: "clickThroughRate",
    label: "Click-Through Rate",
    reportTypeId: "spCampaigns",
    columnName: "clickThroughRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "clicks", denominator: "impressions" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  costPerClick: {
    key: "costPerClick",
    label: "Cost Per Click",
    reportTypeId: "spCampaigns",
    columnName: "costPerClick",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "clicks" },
    compatibleWidgets: ["kpi", "line-chart"],
  },

  // --- Purchases (4) ---
  purchases7d: {
    key: "purchases7d",
    label: "Purchases (7d)",
    reportTypeId: "spCampaigns",
    columnName: "purchases7d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    attributionWindowDays: 7,
  },
  purchases14d: {
    key: "purchases14d",
    label: "Purchases (14d)",
    reportTypeId: "spCampaigns",
    columnName: "purchases14d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    attributionWindowDays: 14,
  },
  unitsSoldClicks7d: {
    key: "unitsSoldClicks7d",
    label: "Units Sold (Clicks, 7d)",
    reportTypeId: "spCampaigns",
    columnName: "unitsSoldClicks7d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    attributionWindowDays: 7,
  },
  unitsSoldClicks14d: {
    key: "unitsSoldClicks14d",
    label: "Units Sold (Clicks, 14d)",
    reportTypeId: "spCampaigns",
    columnName: "unitsSoldClicks14d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    attributionWindowDays: 14,
  },

  // ==========================================================================
  // Story 19.16.1 AC #2 — 8 NEW SP attribution variants (4 × 1d + 4 × 30d).
  // Amazon's Sponsored Products v3 API exposes 1d / 7d / 30d as TOP-LEVEL
  // attribution windows (verified May 2026 via Helium10 + Marin Support +
  // Amazon docs). 14d is column-suffix-only on Same-SKU/Other-SKU/Sales
  // metrics 19.14 already shipped. 3d is NOT API-exposed.
  // ==========================================================================
  purchases1d: {
    key: "purchases1d",
    label: "Purchases (1d)",
    reportTypeId: "spCampaigns",
    columnName: "purchases1d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    attributionWindowDays: 1,
  },
  unitsSoldClicks1d: {
    key: "unitsSoldClicks1d",
    label: "Units Sold (Clicks, 1d)",
    reportTypeId: "spCampaigns",
    columnName: "unitsSoldClicks1d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    attributionWindowDays: 1,
  },
  acosClicks1d: {
    key: "acosClicks1d",
    label: "ACOS Clicks (1d)",
    reportTypeId: "spCampaigns",
    columnName: "acosClicks1d",
    dataType: "Percentage",
    // Story 17.3 review: numerator annotated — see acosClicks7d note.
    aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "sales1d" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
    attributionWindowDays: 1,
  },
  roasClicks1d: {
    key: "roasClicks1d",
    label: "ROAS Clicks (1d)",
    reportTypeId: "spCampaigns",
    columnName: "roasClicks1d",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { numerator: "sales1d", denominator: "cost" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
    attributionWindowDays: 1,
  },
  purchases30d: {
    key: "purchases30d",
    label: "Purchases (30d)",
    reportTypeId: "spCampaigns",
    columnName: "purchases30d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    attributionWindowDays: 30,
  },
  unitsSoldClicks30d: {
    key: "unitsSoldClicks30d",
    label: "Units Sold (Clicks, 30d)",
    reportTypeId: "spCampaigns",
    columnName: "unitsSoldClicks30d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    attributionWindowDays: 30,
  },
  acosClicks30d: {
    key: "acosClicks30d",
    label: "ACOS Clicks (30d)",
    reportTypeId: "spCampaigns",
    columnName: "acosClicks30d",
    dataType: "Percentage",
    // Story 17.3 review: numerator annotated — see acosClicks7d note.
    aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "sales30d" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
    attributionWindowDays: 30,
  },
  roasClicks30d: {
    key: "roasClicks30d",
    label: "ROAS Clicks (30d)",
    reportTypeId: "spCampaigns",
    columnName: "roasClicks30d",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { numerator: "sales30d", denominator: "cost" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
    attributionWindowDays: 30,
  },

  // ==========================================================================
  // Story 35.7 AC #2 — SP 1d/30d attribution-window long-tail (+14 metrics).
  // NOTE on the count: Story 35.7 header line 10 calls this "(12 rows)" but
  // the enumerated AC #2 list across 6 sub-clusters sums to 14 (2+4+2+2+2+2);
  // implementation ships the enumerated set as source-of-truth per the
  // dev-story Cluster 1 reconciliation rationale.
  //
  // 2× Same-SKU Purchases 1d/30d + 4× Same-SKU Units Sold (1d/7d/14d/30d) +
  // 2× Same-SKU Attributed Sales 1d/30d + 2× Other-SKU Units Sold 1d/30d +
  // 2× Other-SKU Sales 1d/30d + 2× Purchase Click Rate 1d/14d = 14.
  // (Code-precedent NOTE: `unitsSoldSameSku7d/14d` were missing from prior
  // 19.14 ship — Story 35.7 closes the asymmetric same-SKU coverage gap
  // discovered during this audit; they are NEW entries, not 1d/30d
  // variants.)
  //
  // Per `_bmad/bmm/data/integration-metrics/amazon-ads.md` § SP Purchases
  // attribution windows (rows #14, #17, #22-25, #26, #29) + § SP Purchased
  // Product Report — Other SKU (rows #34, #37, #38, #41) + § SP Performance
  // Ratios (rows #46, #48). Code-precedent assignment: Same-SKU + Other-SKU
  // siblings live on `spAdvertisedProduct` matching existing 7d/14d entries
  // (build-to-sell directive: code is source-of-truth; if catalog disagrees,
  // catalog is the side that adopts the code's assignment — see Story 35.7
  // § Cluster 4 funnel-event reconciliation rationale).
  //
  // Why `purchaseClickRate*` lives on `spCampaigns`: existing `purchaseClickRate7d`
  // (Story 19.14) is on `spCampaigns`; the 1d/14d siblings adopt the same
  // report-type assignment for cross-window aggregation compatibility.
  // ==========================================================================
  // SP Purchases Same SKU (1d, 30d) — 2 fields
  purchasesSameSku1d: {
    key: "purchasesSameSku1d",
    label: "Purchases Same SKU (1d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "purchasesSameSku1d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
    attributionWindowDays: 1,
  },
  purchasesSameSku30d: {
    key: "purchasesSameSku30d",
    label: "Purchases Same SKU (30d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "purchasesSameSku30d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
    attributionWindowDays: 30,
  },
  // SP Units Sold Same SKU (1d, 7d, 14d, 30d) — 4 fields
  unitsSoldSameSku1d: {
    key: "unitsSoldSameSku1d",
    label: "Units Sold Same SKU (1d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "unitsSoldSameSku1d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
    attributionWindowDays: 1,
  },
  unitsSoldSameSku7d: {
    key: "unitsSoldSameSku7d",
    label: "Units Sold Same SKU (7d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "unitsSoldSameSku7d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
    attributionWindowDays: 7,
  },
  unitsSoldSameSku14d: {
    key: "unitsSoldSameSku14d",
    label: "Units Sold Same SKU (14d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "unitsSoldSameSku14d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
    attributionWindowDays: 14,
  },
  unitsSoldSameSku30d: {
    key: "unitsSoldSameSku30d",
    label: "Units Sold Same SKU (30d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "unitsSoldSameSku30d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
    attributionWindowDays: 30,
  },
  // SP Attributed Sales Same SKU (1d, 30d) — 2 fields
  attributedSalesSameSku1d: {
    key: "attributedSalesSameSku1d",
    label: "Attributed Sales Same SKU (1d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "attributedSalesSameSku1d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
    attributionWindowDays: 1,
  },
  attributedSalesSameSku30d: {
    key: "attributedSalesSameSku30d",
    label: "Attributed Sales Same SKU (30d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "attributedSalesSameSku30d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
    attributionWindowDays: 30,
  },
  // SP Units Sold Other SKU (1d, 30d) — 2 fields. Code-precedent: existing
  // 7d/14d siblings on `spAdvertisedProduct`; new 1d/30d adopt same assignment.
  unitsSoldOtherSku1d: {
    key: "unitsSoldOtherSku1d",
    label: "Units Sold Other SKU (1d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "unitsSoldOtherSku1d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
    attributionWindowDays: 1,
  },
  unitsSoldOtherSku30d: {
    key: "unitsSoldOtherSku30d",
    label: "Units Sold Other SKU (30d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "unitsSoldOtherSku30d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
    attributionWindowDays: 30,
  },
  // SP Sales Other SKU (1d, 30d) — 2 fields.
  salesOtherSku1d: {
    key: "salesOtherSku1d",
    label: "Sales Other SKU (1d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "salesOtherSku1d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
    attributionWindowDays: 1,
  },
  salesOtherSku30d: {
    key: "salesOtherSku30d",
    label: "Sales Other SKU (30d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "salesOtherSku30d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
    attributionWindowDays: 30,
  },
  // SP Purchase Click Rate (1d, 14d) — 2 fields. Derived = purchases/clicks
  // per window; Amazon emits verbatim, adapter does NOT compute.
  purchaseClickRate1d: {
    key: "purchaseClickRate1d",
    label: "Purchase Click Rate (1d)",
    reportTypeId: "spCampaigns",
    columnName: "purchaseClickRate1d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "purchases1d", denominator: "clicks" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
    attributionWindowDays: 1,
  },
  purchaseClickRate14d: {
    key: "purchaseClickRate14d",
    label: "Purchase Click Rate (14d)",
    reportTypeId: "spCampaigns",
    columnName: "purchaseClickRate14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "purchases14d", denominator: "clicks" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
    attributionWindowDays: 14,
  },

  // --- Sales — Total (4) ---
  sales1d: {
    key: "sales1d",
    label: "Sales (1d)",
    reportTypeId: "spCampaigns",
    columnName: "sales1d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  sales7d: {
    key: "sales7d",
    label: "Sales (7d)",
    reportTypeId: "spCampaigns",
    columnName: "sales7d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  sales14d: {
    key: "sales14d",
    label: "Sales (14d)",
    reportTypeId: "spCampaigns",
    columnName: "sales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  sales30d: {
    key: "sales30d",
    label: "Sales (30d)",
    reportTypeId: "spCampaigns",
    columnName: "sales30d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },

  // --- Performance Ratios (5) ---
  acosClicks7d: {
    key: "acosClicks7d",
    label: "ACOS Clicks (7d)",
    reportTypeId: "spCampaigns",
    columnName: "acosClicks7d",
    dataType: "Percentage",
    // Story 17.3 review: ACOS = cost/sales — `numerator: "cost"` annotated so
    // the markup engine classifies ACOS as spend-derived (multiply), matching
    // the SD precedent (matchedTargetACOS14d). Aggregation weighting is
    // denominator-driven and unchanged.
    aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "sales7d" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  acosClicks14d: {
    key: "acosClicks14d",
    label: "ACOS Clicks (14d)",
    reportTypeId: "spCampaigns",
    columnName: "acosClicks14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "sales14d" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  roasClicks7d: {
    key: "roasClicks7d",
    label: "ROAS Clicks (7d)",
    reportTypeId: "spCampaigns",
    columnName: "roasClicks7d",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { numerator: "sales7d", denominator: "cost" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  roasClicks14d: {
    key: "roasClicks14d",
    label: "ROAS Clicks (14d)",
    reportTypeId: "spCampaigns",
    columnName: "roasClicks14d",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { numerator: "sales14d", denominator: "cost" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  purchaseClickRate7d: {
    key: "purchaseClickRate7d",
    label: "Purchase Click Rate (7d)",
    reportTypeId: "spCampaigns",
    columnName: "purchaseClickRate7d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "purchases7d", denominator: "clicks" },
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },

  // --- Other-SKU Sales (4) ---
  salesOtherSku7d: {
    key: "salesOtherSku7d",
    label: "Sales Other SKU (7d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "salesOtherSku7d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
  },
  salesOtherSku14d: {
    key: "salesOtherSku14d",
    label: "Sales Other SKU (14d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "salesOtherSku14d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
  },
  unitsSoldOtherSku7d: {
    key: "unitsSoldOtherSku7d",
    label: "Units Sold Other SKU (7d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "unitsSoldOtherSku7d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
  },
  unitsSoldOtherSku14d: {
    key: "unitsSoldOtherSku14d",
    label: "Units Sold Other SKU (14d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "unitsSoldOtherSku14d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
  },

  // --- Same-SKU Sales (4) ---
  attributedSalesSameSku7d: {
    key: "attributedSalesSameSku7d",
    label: "Attributed Sales Same SKU (7d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "attributedSalesSameSku7d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
  },
  attributedSalesSameSku14d: {
    key: "attributedSalesSameSku14d",
    label: "Attributed Sales Same SKU (14d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "attributedSalesSameSku14d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table", "bar-chart"],
  },
  purchasesSameSku7d: {
    key: "purchasesSameSku7d",
    label: "Purchases Same SKU (7d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "purchasesSameSku7d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
  },
  purchasesSameSku14d: {
    key: "purchasesSameSku14d",
    label: "Purchases Same SKU (14d)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "purchasesSameSku14d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "data-table"],
  },

  // --- 1-Year (4 — only valid on spAdvertisedProduct) ---
  impressions1y: {
    key: "impressions1y",
    label: "Impressions (1y)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "impressions1y",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  clicks1y: {
    key: "clicks1y",
    label: "Clicks (1y)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "clicks1y",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  spend1y: {
    key: "spend1y",
    label: "Spend (1y)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "spend1y",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
  },
  costPerClick1y: {
    key: "costPerClick1y",
    label: "CPC (1y)",
    reportTypeId: "spAdvertisedProduct",
    columnName: "costPerClick1y",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "spend1y", denominator: "clicks1y" },
    compatibleWidgets: ["kpi"],
  },

  // ==========================================================================
  // Story 19.16.1 AC #11 — `*AllViews*` 2026 attribution-change baseline
  // columns. Emitted ONLY when the requested time range crosses 2026-01-01
  // UTC (cross-attribution-model boundary detection). 4 entries for SP.
  // ==========================================================================
  purchasesAllViews7d: {
    key: "purchasesAllViews7d",
    label: "Purchases — All Views (7d)",
    reportTypeId: "spCampaigns",
    columnName: "purchasesAllViews7d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    attributionWindowDays: 7,
  },
  salesAllViews7d: {
    key: "salesAllViews7d",
    label: "Sales — All Views (7d)",
    reportTypeId: "spCampaigns",
    columnName: "salesAllViews7d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    attributionWindowDays: 7,
  },
  purchasesAllViews14d: {
    key: "purchasesAllViews14d",
    label: "Purchases — All Views (14d)",
    reportTypeId: "spCampaigns",
    columnName: "purchasesAllViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    attributionWindowDays: 14,
  },
  salesAllViews14d: {
    key: "salesAllViews14d",
    label: "Sales — All Views (14d)",
    reportTypeId: "spCampaigns",
    columnName: "salesAllViews14d",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    attributionWindowDays: 14,
  },
};

/**
 * v2→v3 rename golden snapshot (AC #9). If Amazon ever ships a column rename,
 * the test that consumes this snapshot fails in CI immediately. Update the
 * snapshot ONLY when the rename has been investigated and SP_V3_COLUMN_MAP
 * has been updated to handle both the old and new names.
 *
 * Source for v2→v3 rename pairs: advertising.amazon.com/API/docs/en-us/reference/migration-guides/reporting-v2-v3
 *
 * Only includes the v2 names that map to OUR 30-metric subset.
 */
export const SP_V2_TO_V3_RENAME_GOLDEN: ReadonlyArray<{
  readonly v2: string;
  readonly v3: string;
}> = [
  { v2: "attributedConversions7d", v3: "purchases7d" },
  { v2: "attributedConversions14d", v3: "purchases14d" },
  { v2: "attributedSales7d", v3: "sales7d" },
  { v2: "attributedSales14d", v3: "sales14d" },
  { v2: "attributedSales1d", v3: "sales1d" },
  { v2: "attributedSales30d", v3: "sales30d" },
  { v2: "attributedUnitsOrdered7d", v3: "unitsSoldClicks7d" },
  { v2: "attributedUnitsOrdered14d", v3: "unitsSoldClicks14d" },
];

// ============================================================================
// Dimension column map (AC #8 — 8 priority dimensions)
// ============================================================================

export interface SpDimensionColumnEntry {
  readonly key: string;
  readonly label: string;
  /** Either a specific report-type the dimension is valid on, or "any" for any SP report. */
  readonly reportTypeId: SpReportTypeId | "any";
  /** Column name on the wire. */
  readonly columnName: string;
  /** Value to send in the request body's `groupBy` array (when applicable). */
  readonly groupByValue: string;
}

/**
 * The 8 priority SP dimensions. Order matches AC #8.
 */
export const SP_V3_DIMENSION_MAP: Record<string, SpDimensionColumnEntry> = {
  campaignId: {
    key: "campaignId",
    label: "Campaign ID",
    reportTypeId: "any",
    columnName: "campaignId",
    groupByValue: "campaign",
  },
  campaignName: {
    key: "campaignName",
    label: "Campaign",
    reportTypeId: "any",
    columnName: "campaignName",
    groupByValue: "campaign",
  },
  campaignStatus: {
    key: "campaignStatus",
    label: "Campaign Status",
    reportTypeId: "any",
    columnName: "campaignStatus",
    groupByValue: "campaign",
  },
  adGroupId: {
    key: "adGroupId",
    label: "Ad Group ID",
    reportTypeId: "any",
    columnName: "adGroupId",
    groupByValue: "adGroup",
  },
  adGroupName: {
    key: "adGroupName",
    label: "Ad Group",
    reportTypeId: "any",
    columnName: "adGroupName",
    groupByValue: "adGroup",
  },
  placement: {
    key: "placement",
    label: "Placement",
    reportTypeId: "spCampaigns",
    columnName: "placement",
    groupByValue: "placement",
  },
  advertisedAsin: {
    key: "advertisedAsin",
    label: "Advertised ASIN",
    reportTypeId: "spAdvertisedProduct",
    columnName: "advertisedAsin",
    groupByValue: "advertiser",
  },
  advertisedSku: {
    key: "advertisedSku",
    label: "Advertised SKU",
    reportTypeId: "spAdvertisedProduct",
    columnName: "advertisedSku",
    groupByValue: "advertiser",
  },
  // Story 19.16.1 AC #3 — Sponsored Products Rufus prompts dimension extension
  // (GA March 25, 2026). Only SP + SB have prompts; SD does NOT. The 256-char
  // truncation invariant is enforced at the cache-payload layer (Round 3
  // Vuln 1 cache-poisoning carry-forward — long prompt text MUST truncate
  // before persistence).
  promptText: {
    key: "promptText",
    label: "Prompt Text",
    reportTypeId: "spCampaigns",
    columnName: "promptText",
    groupByValue: "promptText",
  },
  promptAsin: {
    key: "promptAsin",
    label: "Prompt ASIN",
    reportTypeId: "spCampaigns",
    columnName: "promptAsin",
    groupByValue: "promptAsin",
  },
  // ==========================================================================
  // Story 35.7 AC #7 — 15 NEW dimensions routable through SP report types
  // (Portfolio + Campaign Budget cluster of 6, Keyword family of 9). Lead Gen-
  // specific dims (leadFormId/Type/SchemaVersion) live on SD-side via
  // `SD_V3_DIMENSION_MAP` because they only surface for SD Lead Gen campaigns.
  //
  // Per Code Review Round 1 — these MUST appear in per-product dim maps to be
  // routable through `buildColumnList` (the dispatch path drops unknown keys
  // silently otherwise). Story 35.7 AC #7 enumerates the 18 dims at the
  // umbrella `AMAZON_ADS_DIMENSIONS` level but doesn't separately wire per-
  // product routing — surfacing here closes the dispatch gap.
  //
  // All carry `reportTypeId: "any"` since these dims apply across SP/SB/SD
  // campaign-level reports (verified May 2026 via Amazon Ads v3 OpenAPI).
  // ==========================================================================
  portfolioId: {
    key: "portfolioId",
    label: "Portfolio ID",
    reportTypeId: "any",
    columnName: "portfolioId",
    groupByValue: "campaign",
  },
  campaignBudgetAmount: {
    key: "campaignBudgetAmount",
    label: "Campaign Budget Amount",
    reportTypeId: "any",
    columnName: "campaignBudgetAmount",
    groupByValue: "campaign",
  },
  campaignBudgetCurrencyCode: {
    key: "campaignBudgetCurrencyCode",
    label: "Campaign Budget Currency Code",
    reportTypeId: "any",
    columnName: "campaignBudgetCurrencyCode",
    groupByValue: "campaign",
  },
  campaignRuleBasedBudgetAmount: {
    key: "campaignRuleBasedBudgetAmount",
    label: "Campaign Rule-Based Budget Amount",
    reportTypeId: "any",
    columnName: "campaignRuleBasedBudgetAmount",
    groupByValue: "campaign",
  },
  campaignApplicableBudgetRuleId: {
    key: "campaignApplicableBudgetRuleId",
    label: "Campaign Applicable Budget Rule ID",
    reportTypeId: "any",
    columnName: "campaignApplicableBudgetRuleId",
    groupByValue: "campaign",
  },
  campaignApplicableBudgetRuleName: {
    key: "campaignApplicableBudgetRuleName",
    label: "Campaign Applicable Budget Rule Name",
    reportTypeId: "any",
    columnName: "campaignApplicableBudgetRuleName",
    groupByValue: "campaign",
  },
  keywordId: {
    key: "keywordId",
    label: "Keyword ID",
    reportTypeId: "spTargeting",
    columnName: "keywordId",
    groupByValue: "targeting",
  },
  keyword: {
    key: "keyword",
    label: "Keyword",
    reportTypeId: "spTargeting",
    columnName: "keyword",
    groupByValue: "targeting",
  },
  keywordType: {
    key: "keywordType",
    label: "Keyword Type",
    reportTypeId: "spTargeting",
    columnName: "keywordType",
    groupByValue: "targeting",
  },
  keywordBid: {
    key: "keywordBid",
    label: "Keyword Bid",
    reportTypeId: "spTargeting",
    columnName: "keywordBid",
    groupByValue: "targeting",
  },
  targetingId: {
    key: "targetingId",
    label: "Targeting ID",
    reportTypeId: "spTargeting",
    columnName: "targetingId",
    groupByValue: "targeting",
  },
  targetingText: {
    key: "targetingText",
    label: "Targeting Text",
    reportTypeId: "spTargeting",
    columnName: "targetingText",
    groupByValue: "targeting",
  },
  searchTerm: {
    key: "searchTerm",
    label: "Search Term",
    reportTypeId: "spTargeting",
    columnName: "searchTerm",
    groupByValue: "targeting",
  },
  biddingStrategy: {
    key: "biddingStrategy",
    label: "Campaign Bidding Strategy",
    reportTypeId: "any",
    columnName: "biddingStrategy",
    groupByValue: "campaign",
  },
  campaignBudgetType: {
    key: "campaignBudgetType",
    label: "Campaign Budget Type",
    reportTypeId: "any",
    columnName: "campaignBudgetType",
    groupByValue: "campaign",
  },
};

/**
 * Story 19.16.1 AC #3 — 256-char truncation invariant for prompt text values
 * (Round 3 Vuln 1 cache-poisoning carry-forward). Long prompts MUST truncate
 * before being persisted to KV cache or D1; this constant is the canonical
 * limit so all call sites use the same value.
 */
export const AMAZON_ADS_SP_PROMPT_TEXT_MAX_LENGTH = 256;

/**
 * Truncates a prompt text to the 256-char limit. Use this on every read of
 * `promptText` from Amazon's response BEFORE persisting to cache/D1.
 */
export function truncateSpPromptText(value: string): string {
  if (typeof value !== "string") return "";
  if (value.length <= AMAZON_ADS_SP_PROMPT_TEXT_MAX_LENGTH) return value;
  return value.slice(0, AMAZON_ADS_SP_PROMPT_TEXT_MAX_LENGTH);
}

// ============================================================================
// Public arrays (consumed by getAvailableMetrics / getAvailableDimensions)
// ============================================================================

/** AC #8 — exactly 30 priority SP metrics. */
export const AMAZON_ADS_SP_METRICS: MetricDefinition[] = Object.values(
  SP_V3_COLUMN_MAP
).map((m) => ({
  key: m.key,
  label: m.label,
  type:
    m.dataType === "Currency"
      ? "currency"
      : m.dataType === "Percentage"
        ? "percentage"
        : "number",
  category: "Amazon Ads SP",
  dataType: m.dataType,
  // Story 31-31 — carry aggregation metadata through the projection.
  ...(m.aggregationKind !== undefined ? { aggregationKind: m.aggregationKind } : {}),
  ...(m.ratioComponents !== undefined ? { ratioComponents: m.ratioComponents } : {}),
  compatibleWidgets: [...m.compatibleWidgets],
  apiField: m.columnName,
}));

/** AC #8 — exactly 8 priority SP dimensions. */
/**
 * Story 35.7 — broaden the type heuristic to classify the 15 NEW long-tail
 * dimensions appropriately. Portfolio + Budget cluster → "Structure";
 * Keyword/Targeting/Search family → "Search"; ASIN/SKU → "Product";
 * placement → "Delivery"; campaign* / adGroup* / promptText* → "Structure".
 */
function classifySpDimensionType(key: string): DimensionDefinition["type"] {
  if (key === "placement") return "Delivery";
  // Round 2 — `keywordBid` is a Currency-typed bid amount, NOT a Search
  // criterion. Classify it as Structure metadata alongside Budget/Portfolio.
  if (key === "keywordBid") return "Structure";
  if (
    key.startsWith("keyword") ||
    key.startsWith("targeting") ||
    key === "searchTerm"
  ) {
    return "Search";
  }
  if (key.endsWith("Asin") || key.endsWith("Sku")) return "Product";
  // Default: anything campaign-/portfolio-/budget-/bidding-/prompt-related is
  // campaign Structure metadata.
  return "Structure";
}

export const AMAZON_ADS_SP_DIMENSIONS: DimensionDefinition[] = Object.values(
  SP_V3_DIMENSION_MAP
).map((d) => ({
  key: d.key,
  label: d.label,
  type: classifySpDimensionType(d.key),
}));

// ============================================================================
// Pure helper: pick report type from dimensions (AC #1)
// ============================================================================

export type PickReportTypeResult =
  | { readonly ok: true; readonly reportTypeId: SpReportTypeId; readonly warnings: string[] }
  | { readonly ok: false; readonly error: "unsupported_dimension_combination"; readonly message: string };

/**
 * Deterministic pure function that maps a requested dimension set to the
 * correct SP report type. Order:
 *
 *   1. `purchasedAsin` ∈ dim → spPurchasedProduct
 *   2. `targeting`/`keyword`/`matchType` ∈ dim → spTargeting
 *   3. `advertisedAsin`/`advertisedSku` ∈ dim → spAdvertisedProduct
 *   4. dim ⊆ {campaign*, adGroup*, placement} → spCampaigns
 *   5. otherwise → unsupported_dimension_combination
 *
 * Empty dimension array defaults to spCampaigns (most common request).
 */
export function pickReportType(
  dimensions: ReadonlyArray<string>
): PickReportTypeResult {
  const set = new Set(dimensions);
  const warnings: string[] = [];

  // Classify dimensions into report-type "buckets". Dimensions in the
  // "any" bucket (campaign / adGroup + Story 35.7 Portfolio/Budget cluster)
  // are compatible with every report.
  const ANY_DIMS = new Set([
    "campaignId",
    "campaignName",
    "campaignStatus",
    "adGroupId",
    "adGroupName",
    // Story 35.7 — Portfolio + Campaign Budget cluster routes through "any"
    // because budget/portfolio metadata is exposed on every SP/SB/SD report.
    "portfolioId",
    "campaignBudgetAmount",
    "campaignBudgetCurrencyCode",
    "campaignRuleBasedBudgetAmount",
    "campaignApplicableBudgetRuleId",
    "campaignApplicableBudgetRuleName",
    "biddingStrategy",
    "campaignBudgetType",
  ]);
  const PURCHASED_PRODUCT_DIMS = new Set(["purchasedAsin", "purchasedSku"]);
  // Story 35.7 — Keyword family extends spTargeting buckets.
  const TARGETING_DIMS = new Set([
    "targeting",
    "keyword",
    "matchType",
    "keywordId",
    "keywordType",
    "keywordBid",
    "targetingId",
    "targetingText",
    "searchTerm",
  ]);
  const ADV_PRODUCT_DIMS = new Set(["advertisedAsin", "advertisedSku"]);
  const CAMPAIGNS_ONLY_DIMS = new Set(["placement"]);

  const buckets = new Set<SpReportTypeId>();
  const unknown: string[] = [];
  for (const d of set) {
    if (ANY_DIMS.has(d)) continue;
    if (PURCHASED_PRODUCT_DIMS.has(d)) buckets.add("spPurchasedProduct");
    else if (TARGETING_DIMS.has(d)) buckets.add("spTargeting");
    else if (ADV_PRODUCT_DIMS.has(d)) buckets.add("spAdvertisedProduct");
    else if (CAMPAIGNS_ONLY_DIMS.has(d)) buckets.add("spCampaigns");
    else unknown.push(d);
  }

  if (unknown.length > 0) {
    return {
      ok: false,
      error: "unsupported_dimension_combination",
      message: `Unknown dimension(s): ${unknown.join(", ")}`,
    };
  }

  if (buckets.size > 1) {
    return {
      ok: false,
      error: "unsupported_dimension_combination",
      message: `Dimensions span multiple SP report types (${[...buckets].join(", ")})`,
    };
  }

  if (buckets.size === 1) {
    return {
      ok: true,
      reportTypeId: [...buckets][0]!,
      warnings,
    };
  }

  // Empty buckets → only campaign/adGroup (or nothing) was requested → default
  // to spCampaigns.
  return { ok: true, reportTypeId: "spCampaigns", warnings };
}

/**
 * Returns the columns to request for a given (reportTypeId, requestedMetrics,
 * requestedDimensions) combo. Used by `buildSpReportRequest`.
 */
export function buildColumnList(
  reportTypeId: SpReportTypeId,
  requestedMetricKeys: ReadonlyArray<string>,
  requestedDimensionKeys: ReadonlyArray<string>
): { columns: string[]; warnings: string[] } {
  const warnings: string[] = [];
  const columns = new Set<string>();

  // Always include the dimensions on this report type (so groupBy aligns).
  for (const dk of requestedDimensionKeys) {
    const d = SP_V3_DIMENSION_MAP[dk];
    if (!d) {
      warnings.push(`Unknown dimension key dropped: ${dk}`);
      continue;
    }
    if (d.reportTypeId !== "any" && d.reportTypeId !== reportTypeId) {
      warnings.push(
        `Dimension ${dk} valid on ${d.reportTypeId}, dropped for ${reportTypeId}`
      );
      continue;
    }
    columns.add(d.columnName);
  }

  // Add metric columns that match this report type.
  for (const mk of requestedMetricKeys) {
    const m = SP_V3_COLUMN_MAP[mk];
    if (!m) {
      warnings.push(`Unknown metric key dropped: ${mk}`);
      continue;
    }
    if (m.reportTypeId !== reportTypeId) {
      warnings.push(
        `Metric ${mk} valid on ${m.reportTypeId}, dropped for ${reportTypeId}`
      );
      continue;
    }
    columns.add(m.columnName);
  }

  return { columns: [...columns], warnings };
}

/**
 * Returns the deduped `groupBy` values to send in the request body.
 *
 * Round 2 — when the requested dimensions span multiple `groupBy` values
 * (e.g. an "any" budget dim + a report-specific dim), Amazon's SP v3 API
 * accepts only the report-type-canonical `groupBy` per call; preferentially
 * keep the report-type-canonical entry and drop the "any" -typed budget/
 * portfolio dims' `groupBy: "campaign"` when a more-specific entry exists.
 * The full dimension list still surfaces in the `columns` list — they just
 * don't drive grouping when they conflict with the report-type's canonical
 * grouping axis.
 */
export function buildGroupByList(
  reportTypeId: SpReportTypeId,
  requestedDimensionKeys: ReadonlyArray<string>
): string[] {
  // Bucket entries by whether the dim is report-type-canonical or "any"-shared.
  const canonical = new Set<string>();
  const sharedAny = new Set<string>();
  for (const dk of requestedDimensionKeys) {
    const d = SP_V3_DIMENSION_MAP[dk];
    if (!d) continue;
    if (d.reportTypeId !== "any" && d.reportTypeId !== reportTypeId) continue;
    if (d.reportTypeId === reportTypeId) canonical.add(d.groupByValue);
    else sharedAny.add(d.groupByValue);
  }
  // If a canonical groupBy exists, drop the shared-any "campaign" defaults
  // (they'd produce a multi-axis groupBy that Amazon's SP v3 API rejects for
  // most report types).
  const out = canonical.size > 0 ? canonical : sharedAny;
  // spCampaigns minimum is "campaign"; spAdvertisedProduct minimum is "advertiser"
  if (out.size === 0) {
    if (reportTypeId === "spCampaigns") out.add("campaign");
    else if (reportTypeId === "spAdvertisedProduct") out.add("advertiser");
    else if (reportTypeId === "spTargeting") out.add("targeting");
    else if (reportTypeId === "spPurchasedProduct") out.add("asin");
  }
  return [...out];
}
